import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { User, Lock, Eye, EyeOff, ShieldCheck, Trophy, Headphones } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, TrustBar } from "@/components/brand";

export const IconInput = ({ icon: Icon, right, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" />}
    <input {...props} className={`input-cr ${Icon ? "pl-10" : ""} ${right ? "pr-11" : ""}`} />
    {right}
  </div>
);

export const PasswordInput = ({ testid, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <IconInput icon={Lock} type={show ? "text" : "password"} data-testid={testid} {...props}
      right={<button type="button" data-testid={`${testid}-toggle`} aria-label={show ? "Hide password" : "Show password"} onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-cr-muted hover:text-cr-gold">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>} />
  );
};


const Perks = () => (
  <div className="mt-10 grid grid-cols-3 gap-4 text-center">
    {[[ShieldCheck, "Secure & Safe", "Protected accounts"], [Trophy, "Top Sports", "Football to esports"], [Headphones, "Support", "Ticket-based help"]].map(([I, t, d]) => (
      <div key={t}><I className="mx-auto h-8 w-8 text-cr-gold" /><p className="mt-2 font-display text-sm font-bold uppercase text-cr-gold">{t}</p><p className="text-xs text-cr-ink/70">{d}</p></div>
    ))}
  </div>
);

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ identifier: "", password: "", remember: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.identifier || !f.password) return setErr("Enter your email/username and password");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      toast.success("Welcome back!");
      nav(loc.state?.from || "/account", { replace: true });
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-cr-bg p-3 sm:p-5">
      <div className="relative mx-auto flex min-h-[calc(100vh-2.5rem)] max-w-7xl flex-col overflow-hidden rounded-2xl border border-cr-gold/30">
        <img src="/brand/stadium.jpg" alt="" className="absolute inset-y-0 left-0 hidden h-full w-[45%] object-cover opacity-40 lg:block" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#080909]/80 to-[#080909]" />
        <div className="relative grid flex-1 items-center gap-10 p-6 lg:grid-cols-2 lg:p-14">
          <div className="hidden text-center lg:block">
            <img src="/brand/logo.png" alt="CRBETS" className="mx-auto h-52 mix-blend-lighten" />
            <h2 className="mt-6 text-3xl font-extrabold uppercase">Play. Predict. <span className="gold-text">Win Big.</span></h2>
            <p className="mx-auto mt-3 max-w-sm text-cr-ink/75">Your home for sports entertainment and casino-style games. Please play responsibly.</p>
            <Perks />
          </div>
          <div className="mx-auto w-full max-w-md">
            <div className="mb-6 flex justify-center lg:hidden"><Logo className="h-20" /></div>
            <form onSubmit={submit} noValidate className="card-gold p-7 sm:p-9" data-testid="login-form">
              <h1 className="text-center text-3xl font-bold">Welcome Back</h1>
              <p className="mt-2 text-center text-cr-ink/75">Login to continue to CR BETS</p>
              <label className="mt-8 block text-sm font-semibold">Email or Username</label>
              <div className="mt-2"><IconInput icon={User} data-testid="login-identifier-input" autoComplete="username" placeholder="Enter your email or username" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} /></div>
              <div className="mt-5 flex items-center justify-between">
                <label className="text-sm font-semibold">Password</label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm text-cr-gold hover:underline">Forgot Password?</Link>
              </div>
              <div className="mt-2"><PasswordInput testid="login-password-input" autoComplete="current-password" placeholder="Enter your password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
              <label className="mt-5 flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="login-remember-checkbox" checked={f.remember} onChange={(e) => setF({ ...f, remember: e.target.checked })} className="h-4 w-4 accent-[#FFC928]" />Remember Me
              </label>
              {err && <p data-testid="login-error" role="alert" className="mt-4 rounded-md border border-cr-danger/40 bg-cr-danger/10 px-3 py-2 text-sm text-cr-danger">{err}</p>}
              <button disabled={busy} data-testid="login-submit-btn" className="btn-gold mt-6 w-full py-3.5 text-base">{busy ? "Signing in…" : "Login"}</button>
              <p className="mt-6 text-center text-cr-ink/80">Don't have an account? <Link to="/register" data-testid="login-signup-link" className="font-bold text-cr-gold">Sign Up</Link></p>
            </form>
          </div>
        </div>
        <div className="relative px-6 lg:px-14"><TrustBar /></div>
      </div>
    </div>
  );
}
